import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    HiOutlineChevronLeft, HiOutlineRefresh, HiOutlineShieldCheck,
    HiOutlineDownload, HiOutlineX, HiOutlineSearch
} from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import './ActionLog.css';

const OUTCOME_LABEL = { success: 'Success', denied: 'Denied', error: 'Error' };

// Table cell shows the timestamp on two deliberate lines (date over time)
// instead of clipping; the drawer still shows the full localized string.
const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, {
    year: '2-digit', month: 'short', day: 'numeric',
});
const fmtTime = (d) => new Date(d).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const countChanges = (it) => (Array.isArray(it.changes)
    ? it.changes.reduce((n, c) => n + (c.fields?.length || (c.op === 'bulk' ? 1 : 0)), 0)
    : 0);
const EMPTY_FILTERS = {
    q: '', username: '', card: '', action: '', outcome: '', method: '',
    model: '', targetId: '', from: '', to: ''
};

export default function ActionLog() {
    const navigate = useNavigate();

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0, totalPages: 1 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [options, setOptions] = useState({ cards: [], actions: [], outcomes: [] });
    const [selected, setSelected] = useState(null);
    const [verify, setVerify] = useState(null);
    const [verifying, setVerifying] = useState(false);
    // Phones only (see ActionLog.css): ten filters would otherwise fill the
    // first screen and push the table below it.
    const [filtersOpen, setFiltersOpen] = useState(false);
    const activeFilters = Object.values(applied).filter(Boolean).length;

    useEffect(() => {
        axiosInstance.get('/api/action-log/meta/filters')
            .then((r) => setOptions(r.data))
            .catch(() => {});
    }, []);

    const query = useMemo(() => {
        const p = { page, limit: 50 };
        Object.entries(applied).forEach(([k, v]) => { if (v) p[k] = v; });
        return p;
    }, [applied, page]);

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        axiosInstance.get('/api/action-log', { params: query })
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.message || 'Failed to load action log'))
            .finally(() => setLoading(false));
    }, [query]);

    useEffect(() => { load(); }, [load]);

    const runFilter = (e) => {
        e?.preventDefault();
        setPage(1);
        setApplied(filters);
        setFiltersOpen(false);
    };
    const clearFilter = () => {
        setFilters(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
        setPage(1);
    };

    const runVerify = () => {
        setVerifying(true);
        setVerify(null);
        axiosInstance.get('/api/action-log/verify')
            .then((r) => setVerify(r.data.report))
            .catch((e) => setVerify({ ok: false, reason: e.response?.data?.message || 'Verification failed' }))
            .finally(() => setVerifying(false));
    };

    const exportCsv = () => {
        const p = new URLSearchParams();
        Object.entries(applied).forEach(([k, v]) => { if (v) p.append(k, v); });
        axiosInstance.get(`/api/action-log/export.csv?${p.toString()}`, { responseType: 'blob' })
            .then((r) => {
                const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `action-log-${Date.now()}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => setError('CSV export failed'));
    };

    const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div className="al-shell">
            <header className="al-header">
                <button className="al-back" onClick={() => navigate('/purpose-selection')}>
                    <HiOutlineChevronLeft /> Back
                </button>
                <div className="al-title">
                    <HiOutlineShieldCheck />
                    <div>
                        <h1>Action Log</h1>
                        <p>Append-only, hash-chained record of every operation</p>
                    </div>
                </div>
                <div className="al-actions">
                    <button className="al-btn" onClick={runVerify} disabled={verifying}>
                        <HiOutlineShieldCheck /> {verifying ? 'Verifying…' : 'Verify integrity'}
                    </button>
                    <button className="al-btn" onClick={exportCsv}>
                        <HiOutlineDownload /> Export CSV
                    </button>
                    <button className="al-btn" onClick={load}>
                        <HiOutlineRefresh /> Refresh
                    </button>
                    <button
                        className={`al-btn al-filter-toggle${activeFilters ? ' active' : ''}`}
                        onClick={() => setFiltersOpen((o) => !o)}
                        aria-expanded={filtersOpen}
                    >
                        <HiOutlineSearch /> Filters{activeFilters ? ` (${activeFilters})` : ''}
                    </button>
                </div>
            </header>

            {verify && (
                <div className={`al-verify ${verify.ok ? 'ok' : 'bad'}`}>
                    {verify.ok ? (
                        <span><strong>Chain intact.</strong> {verify.checked} entries verified
                            {verify.from != null && ` (seq ${verify.from}–${verify.to})`}.
                            {verify.legacy > 0 && ` ${verify.legacy} older bulk ${verify.legacy === 1 ? 'entry' : 'entries'} verified against the earlier hash format.`}</span>
                    ) : (
                        <span><strong>Integrity check FAILED.</strong> {verify.reason}
                            {verify.brokenSeq != null && ` First broken entry: seq ${verify.brokenSeq}.`}</span>
                    )}
                    <button onClick={() => setVerify(null)}><HiOutlineX /></button>
                </div>
            )}

            <form className={`al-filters${filtersOpen ? ' open' : ''}`} onSubmit={runFilter} autoComplete="off">
                <label>Search
                    <input name="q" placeholder="Label or path" value={filters.q} onChange={setF('q')} />
                </label>
                <label>Username
                    <input name="username" placeholder="Any user" value={filters.username} onChange={setF('username')} />
                </label>
                <label>Module
                    <select name="card" value={filters.card} onChange={setF('card')}>
                        <option value="">All modules</option>
                        {options.cards.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label>Action
                    <select name="action" value={filters.action} onChange={setF('action')}>
                        <option value="">All actions</option>
                        {options.actions.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </label>
                <label>Outcome
                    <select name="outcome" value={filters.outcome} onChange={setF('outcome')}>
                        <option value="">All outcomes</option>
                        {options.outcomes.map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o] || o}</option>)}
                    </select>
                </label>
                <label>Method
                    <select name="method" value={filters.method} onChange={setF('method')}>
                        <option value="">Any method</option>
                        {['POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </label>
                <label>Target model
                    <input name="model" placeholder="e.g. Cheque" value={filters.model} onChange={setF('model')} />
                </label>
                <label>Target id
                    <input name="targetId" placeholder="Record id" value={filters.targetId} onChange={setF('targetId')} />
                </label>
                <label>From
                    <input name="from" type="datetime-local" value={filters.from} onChange={setF('from')} />
                </label>
                <label>To
                    <input name="to" type="datetime-local" value={filters.to} onChange={setF('to')} />
                </label>
                <div className="al-filter-actions">
                    <button type="submit" className="al-btn primary"><HiOutlineSearch /> Filter</button>
                    <button type="button" className="al-btn" onClick={clearFilter}>Clear</button>
                </div>
            </form>

            {error && <div className="al-error">{error}</div>}

            <div className="al-table-wrap">
                <table className="al-table">
                    <colgroup>
                        <col className="c-seq" /><col className="c-time" /><col className="c-actor" />
                        <col className="c-module" /><col className="c-request" /><col className="c-target" />
                        <col className="c-changes" /><col className="c-outcome" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="num">#</th><th>Time</th><th>Actor</th><th>Module / Action</th>
                            <th>Request</th><th>Target</th><th className="num">Changes</th><th>Outcome</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={8} className="al-muted">Loading…</td></tr>}
                        {!loading && data.items.length === 0 && (
                            <tr><td colSpan={8} className="al-muted">No entries match.</td></tr>
                        )}
                        {!loading && data.items.map((it) => {
                            const isAuth = it.card === 'auth';
                            const nChanges = countChanges(it);
                            return (
                                <tr key={it.seq} onClick={() => setSelected(it)}>
                                    <td className="num al-sub">{it.seq}</td>
                                    <td className="al-cell-time" title={new Date(it.at).toLocaleString()}>
                                        <div className="al-time-date">{fmtDate(it.at)}</div>
                                        <div>{fmtTime(it.at)}</div>
                                    </td>
                                    <td>
                                        <div className="al-actor">{it.actor?.name || it.actor?.username || '—'}</div>
                                        <div className="al-sub">{it.actor?.role || (it.source !== 'http' ? it.source : '')}</div>
                                    </td>
                                    <td>
                                        {isAuth ? (
                                            <div>Authentication</div>
                                        ) : (
                                            <>
                                                <div>{it.card}</div>
                                                <div className="al-sub">{it.action}</div>
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        <div className="al-req">
                                            <span className={`al-method m-${it.method}`}>{it.method}</span>
                                            <span className="al-path">{it.path}</span>
                                        </div>
                                    </td>
                                    <td>
                                        {it.target?.id ? (
                                            <>
                                                {it.target?.model && <div className="al-sub">{it.target.model}</div>}
                                                <div className="al-target-id">{it.target.id}</div>
                                            </>
                                        ) : <span className="al-dash">—</span>}
                                    </td>
                                    <td className="num">{nChanges || <span className="al-dash">—</span>}</td>
                                    <td><span className={`al-badge ${it.outcome}`}>{OUTCOME_LABEL[it.outcome] || it.outcome}</span></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Phones get cards instead of the 8-column table (ActionLog.css
                swaps them) — only what identifies an entry; the target, hashes
                and field diffs are one tap away in the drawer. */}
            <div className="al-cards">
                {loading && <div className="al-muted">Loading…</div>}
                {!loading && data.items.length === 0 && <div className="al-muted">No entries match.</div>}
                {!loading && data.items.map((it) => {
                    const nChanges = countChanges(it);
                    return (
                        <button key={it.seq} type="button" className="al-card" onClick={() => setSelected(it)}>
                            <div className="al-card-top">
                                <span className="al-card-module">
                                    {it.card === 'auth' ? 'Authentication' : it.card}
                                    {it.card !== 'auth' && it.action && <span className="al-sub"> · {it.action}</span>}
                                </span>
                                <span className={`al-badge ${it.outcome}`}>{OUTCOME_LABEL[it.outcome] || it.outcome}</span>
                            </div>
                            <div className="al-actor">{it.actor?.name || it.actor?.username || '—'}</div>
                            <div className="al-req">
                                <span className={`al-method m-${it.method}`}>{it.method}</span>
                                <span className="al-path">{it.path}</span>
                            </div>
                            <div className="al-card-foot al-sub">
                                <span>{fmtDate(it.at)} · {fmtTime(it.at)}</span>
                                <span>#{it.seq}{nChanges ? ` · ${nChanges} change${nChanges === 1 ? '' : 's'}` : ''}</span>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="al-pager">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
                <span>Page {page} / {data.totalPages || 1} · {data.total} entries</span>
                <button disabled={page >= (data.totalPages || 1)} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>

            {selected && <EntryDrawer entry={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}

function EntryDrawer({ entry, onClose }) {
    return (
        <div className="al-drawer-backdrop" onClick={onClose}>
            <aside className="al-drawer" onClick={(e) => e.stopPropagation()}>
                <div className="al-drawer-head">
                    <h2>Entry #{entry.seq}</h2>
                    <button onClick={onClose}><HiOutlineX /></button>
                </div>

                <dl className="al-kv">
                    <dt>Time</dt><dd>{new Date(entry.at).toLocaleString()} ({entry.atQatar} Qatar)</dd>
                    <dt>Actor</dt><dd>{entry.actor?.name || '—'} ({entry.actor?.username || 'system'}) · {entry.actor?.role || entry.source}</dd>
                    <dt>IP</dt><dd>{entry.ip || '—'}</dd>
                    <dt>User agent</dt><dd className="al-mono">{entry.userAgent || '—'}</dd>
                    <dt>Request</dt><dd>{entry.method} {entry.path}</dd>
                    <dt>Module</dt><dd>{entry.card} · {entry.action}</dd>
                    <dt>Outcome</dt><dd>{entry.outcome} (HTTP {entry.status}){entry.error ? ` — ${entry.error}` : ''}</dd>
                    <dt>Target</dt><dd>{entry.target?.model || '?'} {entry.target?.id || '—'} {entry.target?.display ? `(${entry.target.display})` : ''}</dd>
                    <dt>Hash</dt><dd className="al-mono al-break">{entry.hash}</dd>
                    <dt>Prev hash</dt><dd className="al-mono al-break">{entry.prevHash}</dd>
                </dl>

                <h3>Document changes</h3>
                {(!entry.changes || entry.changes.length === 0) && <p className="al-muted">No document-level changes captured.</p>}
                {entry.changes && entry.changes.map((c, i) => (
                    <div key={i} className="al-change">
                        <div className="al-change-head">{c.op.toUpperCase()} · {c.model || '?'} · {c.id || '—'}</div>
                        {c.op === 'bulk' ? (
                            <div className="al-sub">matched {c.matched} document(s) · filter {JSON.stringify(c.filter)}</div>
                        ) : (
                            <table className="al-diff">
                                <thead><tr><th>Field</th><th>From</th><th>To</th></tr></thead>
                                <tbody>
                                    {(c.fields || []).map((f, j) => (
                                        <tr key={j}>
                                            <td className="al-mono">{f.path}</td>
                                            <td><Val v={f.from} /></td>
                                            <td><Val v={f.to} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                ))}

                <h3>Request payload</h3>
                <pre className="al-json">{JSON.stringify(entry.meta || {}, null, 2)}</pre>
            </aside>
        </div>
    );
}

function Val({ v }) {
    if (v === null || v === undefined) return <span className="al-muted">∅</span>;
    if (typeof v === 'object') return <span className="al-mono">{JSON.stringify(v)}</span>;
    return <span className="al-mono">{String(v)}</span>;
}
